<template>
  <div class="mb-2">
    <validation-observer ref="formModalObserver" novalidate tag="form">
      <div class="row">
        <div class="col-12 mb-3">
          <icon-view
            :has-border="false"
            icon="fas fa-map-marker-alt text-orange fa-lg"
            title="Actualizar Localidad"
            variant="custom-icon-view"
            icon-background="white"
          />
        </div>
        <div class="col-12 col-md-4">
          <validation-provider
            v-slot="{ errors }"
            mode="eager"
            rules="required"
          >
            <custom-drop-down
              label="Centro:"
              fieldkey="id"
              fieldname="name"
              placeholder="Seleccione"
              class="w-100 mb-4 bold-label"
              custom-error-msg="Favor seleccione un centro"
              v-model="model.center"
              :options="collections.centers"
              :error="errors[0]"
              @input="(val) => getMunicipalities(val?.id)"
            />
          </validation-provider>
        </div>
        <div
          v-if="isLocationSchool || areOnlySchoolLocationTypeChecked"
          class="col-12 col-md-4"
        >
          <custom-drop-down
            label="Municipio:"
            fieldkey="id"
            fieldname="name"
            placeholder="Seleccione"
            class="w-100 mb-4 bold-label"
            v-model="model.municipality"
            :options="collections.municipalities"
            @input="(val) => getSchools(val?.id)"
          />
        </div>
        <div
          v-if="isLocationSchool || areOnlySchoolLocationTypeChecked"
          class="col-12 col-md-4"
        >
          <validation-provider
            v-slot="{ errors }"
            mode="eager"
            :rules="hasMunicipality ? 'required' : ''"
          >
            <custom-drop-down
              label="Escuela:"
              fieldkey="id"
              fieldname="name"
              placeholder="Seleccione"
              class="w-100 mb-4 bold-label"
              custom-error-msg="Favor seleccione una escuela"
              v-model="model.school"
              :options="collections.schools"
              :error="errors[0]"
            />
          </validation-provider>
        </div>
      </div>
    </validation-observer>
    <div class="d-flex justify-content-end align-items-center mt-4">
      <action-button
        id="btnCancel"
        i="fas fa-times-circle"
        text="Cancelar"
        variant="outline-danger mr-3 px-3"
        @click="$emit('close')"
      />
      <action-button
        id="btnSave"
        i="fas fa-save"
        text="Guardar"
        variant="success px-3"
        @click="save"
      />
    </div>
  </div>
</template>

<script>
import ActionButton from 'ActionButton';
import CustomDropDown from 'custom-drop-down';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
import inventoryCatalogService from '@/services/api/inventorycatalog.api';
import IconView from 'icon-view';
import locationService from '@/services/api/location.api';
import LocationTypes from '@/utils/constants/inventory/inventoryOrderBookLocationTypes';

export default {
  name: 'UpdateLocation',
  components: {
    IconView,
    ActionButton,
    CustomDropDown,
  },
  mixins: [alertMixin],
  props: {
    item: {
      type: Object,
      required: false,
      default: () => null,
    },
    books: {
      type: Array,
      required: false,
      default: () => [],
    },
    areOnlySchoolLocationTypeChecked: {
      type: Boolean,
      required: false,
      default: () => false,
    },
  },
  data() {
    return {
      collections: {
        centers: [],
        municipalities: [],
        schools: [],
      },
      model: {
        center: null,
        municipality: null,
        school: null,
      },
    };
  },
  mounted() {
    this.initData();
  },
  methods: {
    async initData() {
      await this.getCenters();
      if (!this.item?.caatId) return;

      this.model.center = this.collections.centers.find(
        (x) => x.id === this.item.caatId
      );
      await this.getMunicipalities(this.item.caatId);

      if (!this.item.municipalityId) return;

      this.model.municipality = this.collections.municipalities.find(
        (x) => x.id === this.item.municipalityId
      );
      await this.getSchools(this.item.municipalityId);

      if (!this.item.schoolId) return;

      this.model.school = this.collections.schools.find(
        (x) => x.id === this.item.schoolId
      );
    },
    async save() {
      const isvalid = await this.$refs.formModalObserver.validate();
      if (!isvalid) {
        this.ShowToast(
          'Alerta',
          'Por favor completa los campos requeridos.',
          'error'
        );
        return;
      }
      if (this.item == null && this.books.length == 0) {
        this.ShowToast(
          'Alerta',
          'Por favor seleccione al menos un libro en estatus disponible o procesado.',
          'error'
        );
        return;
      }

      const confirm = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea realizar la actualización para ${this.confirmMsg}`,
        'Sí',
        'No'
      );

      if (!confirm) return;
      try {
        const { center, municipality, school } = this.model;
        const payload = {
          bookIds: this.item ? [this.item.id] : this.books.map((x) => x.id),
          centerId: center?.id,
          municipalityId: municipality?.id,
          schoolId: school?.id,
        };
        await inventoryCatalogService.updateLocation(payload);
        this.ShowToast(
          'Éxito',
          'La localidad has sido actualizada exitosamente.',
          'success'
        );
        this.$emit('close');
      } catch (error) {
        this.ShowToast('Error', error?.response?.data.message, 'error');
      }
    },
    async getCenters() {
      const { data } = await locationService.getCenters({
        academicYearId: this.academicYearId,
      });
      this.collections.centers = data;
    },
    async getMunicipalities(centerId = null) {
      this.model.municipality = null;
      this.collections.municipalities = [];
      this.model.school = null;
      this.collections.schools = [];

      if (!centerId) return;

      const sameCenter =
        this.collections.municipalities.length &&
        this.collections.municipalities.every((s) => s.centerId === centerId);

      if (sameCenter) return;

      const { data } = await locationService.getMunicipalities({
        academicYearId: this.academicYearId,
        centerId,
      });
      this.collections.municipalities = centerId
        ? data.map((x) => ({ ...x, centerId }))
        : data;
    },
    async getSchools(municipalityId = null) {
      this.model.school = null;
      this.collections.schools = [];

      if (!municipalityId) return;

      const sameMunicipality =
        this.collections.schools.length &&
        this.collections.schools.every(
          (s) => s.municipalityId === municipalityId
        );
      if (sameMunicipality) return;

      const { data } = await locationService.getSchools({
        academicYearId: this.academicYearId,
        municipalityId,
      });
      this.collections.schools = municipalityId
        ? data.map((x) => ({ ...x, municipalityId }))
        : data;
    },
  },
  computed: {
    academicYearId() {
      return this.currentAcademicYear().id;
    },
    confirmMsg() {
      if (this.item) return `el libro <b>${this.item.code}</b>?`;
      if (this.books.length === 1)
        return `el libro <b>${this.books[0].code}</b>?`;
      const ul = document.createElement('ul');
      ul.className = 'mt-2 mb-0 pl-3';
      this.books.forEach((x) => {
        const ol = document.createElement('li');
        ol.innerHTML = `<b>${x.code}</b>`;
        ul.appendChild(ol);
      });
      return `los libros seleccionados? ${ul.outerHTML}`;
    },
    hasMunicipality() {
      return Boolean(this.model.municipality);
    },
    isLocationSchool() {
      return Boolean(this.item?.locationTypeId === LocationTypes.SCHOOL.id);
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .dot {
  padding: 19px !important;
}
</style>
